import {observer} from 'mobx-react';
import {useContext, useEffect, useMemo, useState} from 'react';
import {NetworkContext} from '../lib/context';
import {
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    TableContainer,
    DataTableSkeleton,
    DataTable,
    InlineNotification,
} from '@carbon/react';
import getFormatter from '../../../lib/formatter';
import CustomPagination from '../../../components/custom-pagination/CustomPagination';

const PAGE_SIZE = 15;

const RosterComparisonDisruptionAnalysis = observer(() => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const networkStore = useContext(NetworkContext);

    const DISRUPTION_ANALYSIS_COMPARISON_HEADERS = [
        {key: 'specialty', header: 'Specialty'},
        {key: 'members', header: 'Members', type: 'number'},
        {key: 'providerLocations', header: 'Provider Locations', type: 'number'},
        {key: 'visits', header: 'Visits', type: 'number'},
        {key: 'allowed($)', header: 'Allowed ($)', type: 'currency'},
        {key: '%MemberImpact(Actual)', header: '% Member Impact (Actual)'},
        {key: '%ProviderLocation(Actual)', header: '% Provider Location (Actual)'},
        {key: '%Utilization(Actual)', header: 'Utilization'},
        {key: '%AllowedImpact(Actual)', header: 'Allowed Impact (Actual)'},
    ];

    useEffect(() => {
        const controller = new AbortController();
        let isActive = true;
        const loadData = async () => {
            setError(null);
            try {
                if (isActive) setLoading(true);
                await networkStore.loadDisruptionAnalysisComparison(controller);
            } catch (err: any) {
                if (!(err && err.name === 'AbortError')) {
                    setError('Failed to load disruption analysis comparison data');
                }
            } finally {
                if (isActive) setLoading(false);
            }
        };
        loadData();
        return () => {
            isActive = false;
            controller.abort();
        };
    }, [
        networkStore.disruptionAnalysisFilters,
        networkStore.disruptionAnalysisSpecialtyFilter,
        networkStore.selectedRosters,
    ]);

    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            try {
                await networkStore.loadDisruptionAnalysisSpecialtiesData(controller);
            } catch (err) {
                console.error(
                    'Failed to load disruption analysis specialty data. Please try again later.',
                );
            }
        };

        loadData();
        return () => controller.abort();
    }, [
        networkStore.appliedDisruptionAnalysisFilter.state,
        networkStore.runId,
        networkStore.revisionId,
        networkStore.readyToLoadData(),
    ]);

    const rows = useMemo(() => {
        const rawData = networkStore.rosterComparisonDisruptionAnalysisData || [];
        const start = page * pageSize;
        const end = start + pageSize;
        return rawData.slice(start, end).map((item: any, idx: number) => ({
            id: idx,
            ...item,
        }));
    }, [networkStore.rosterComparisonDisruptionAnalysisData, page, pageSize]);

    const selectedRosterIds = networkStore.selectedRosters || [];
    const showComparison = selectedRosterIds.length >= 2;

    return (
        <div className='flex flex-1 bg-background-gray-100 relative'>
            <div className='flex-1 p-6'>
                <div className='grid grid-cols-12 gap-6'>
                    <div className='col-span-3'>
                        <div className='bg-gray-50 p-4 h-full'>
                            <h4 className='text-lg font-semibold mb-4'>
                                Disruption Analysis Summary
                            </h4>
                            {showComparison ? (
                                <div
                                    className='space-y-3 text-sm'
                                    data-testid='disruption-analysis-summary-details'
                                >
                                    <div>• 12 differences found</div>
                                    <div>
                                        • 12 cardiology counties where Roster A is compliant and
                                        Roster B is not
                                    </div>
                                    <div>• Average provider cost 8% higher in Roster B</div>
                                    <div>
                                        • 4 dermatology counties where Roster B is compliant and
                                        Roster A is not
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className='text-sm text-gray-500'
                                    data-testid='disruption-analysis-summary-empty'
                                >
                                    <p>Select at least 2 rosters to see comparison metrics.</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='col-span-8'>
                        {showComparison ? (
                            <>
                                {error ? (
                                    <div className='bg-white p-8 rounded-lg shadow-sm flex items-center justify-center h-64 mt-8'>
                                        <div className='w-full max-w-md'>
                                            <InlineNotification
                                                kind='error'
                                                title='Error Loading Data'
                                                subtitle={error}
                                                hideCloseButton={false}
                                                onCloseButtonClick={() => setError(null)}
                                            />
                                        </div>
                                    </div>
                                ) : loading ? (
                                    <DataTableSkeleton
                                        headers={DISRUPTION_ANALYSIS_COMPARISON_HEADERS}
                                        columnCount={DISRUPTION_ANALYSIS_COMPARISON_HEADERS.length}
                                        rowCount={10}
                                        showHeader={true}
                                        showToolbar={false}
                                    />
                                ) : (
                                    <TableContainer data-testid='disruption-analysis-table-container'>
                                        <DataTable
                                            rows={rows}
                                            headers={DISRUPTION_ANALYSIS_COMPARISON_HEADERS}
                                        >
                                            {({headers}) => (
                                                <>
                                                    <Table className='min-w-full'>
                                                        <TableHead>
                                                            <TableRow className='sticky table top-0 bg-white z-10'>
                                                                {headers.map((header) => (
                                                                    <TableHeader
                                                                        key={header.key}
                                                                        className={`bg-white z-20 border-b border-gray-300 text-left p-2`}
                                                                        style={{
                                                                            width: `${
                                                                                header.key ===
                                                                                'specialty'
                                                                                    ? '20%'
                                                                                    : `${70 / (headers.length - 1)}%`
                                                                            }`,
                                                                        }}
                                                                    >
                                                                        {header.header}
                                                                    </TableHeader>
                                                                ))}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody className='block max-h-[60vh] overflow-y-auto'>
                                                            {rows.length === 0 ? (
                                                                <TableRow className='table w-full'>
                                                                    <TableCell
                                                                        colSpan={
                                                                            DISRUPTION_ANALYSIS_COMPARISON_HEADERS.length
                                                                        }
                                                                        className='p-3'
                                                                        style={{
                                                                            textAlign: 'center',
                                                                            fontSize: '1rem',
                                                                            fontWeight: '500',
                                                                        }}
                                                                    >
                                                                        No Data Available
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                rows.map((row) => (
                                                                    <TableRow
                                                                        key={row.id}
                                                                        className='table w-full'
                                                                    >
                                                                        {DISRUPTION_ANALYSIS_COMPARISON_HEADERS.map(
                                                                            (header) => {
                                                                                const formatter =
                                                                                    header?.type
                                                                                        ? getFormatter(
                                                                                            header.type,
                                                                                        )
                                                                                        : {
                                                                                            format: (
                                                                                                value: any,
                                                                                            ) =>
                                                                                                value,
                                                                                        };
                                                                                return (
                                                                                    <TableCell
                                                                                        key={
                                                                                            header.key
                                                                                        }
                                                                                        className='p-2'
                                                                                        style={{
                                                                                            width: `${header.key ===
                                                                                                'specialty'
                                                                                                ? '20%'
                                                                                                : `${70 /
                                                                                                (DISRUPTION_ANALYSIS_COMPARISON_HEADERS.length - 1)
                                                                                                }%`
                                                                                            }`,
                                                                                        }}
                                                                                    >
                                                                                        {formatter.format(
                                                                                            row[
                                                                                                header
                                                                                                    .key
                                                                                            ],
                                                                                        )}
                                                                                    </TableCell>
                                                                                );
                                                                            },
                                                                        )}
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                    {!loading && !error && rows.length > 0 && (
                                                        <CustomPagination
                                                            totalItems={
                                                                networkStore
                                                                    .rosterComparisonDisruptionAnalysisData
                                                                    ?.length || 0
                                                            }
                                                            pageSize={pageSize}
                                                            page={page}
                                                            pageSelectorType='Input'
                                                            onPageSizeChange={(
                                                                newPageSize: number,
                                                            ) => {
                                                                setPageSize(newPageSize);
                                                            }}
                                                            onPageChange={(newPage: number) => {
                                                                setPage(newPage);
                                                            }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </DataTable>
                                    </TableContainer>
                                )}
                                {/* Table Footer */}
                                {!loading && !error && (
                                    <div
                                        className='py-4 text-xs text-gray-500 italic px-4 bg-white'
                                        data-testid='disruption-analysis-table-footer'
                                    >
                                        *Note: "Telemedicine Credit" indicates a 10% credit was
                                        applied to the specialty
                                    </div>
                                )}
                            </>
                        ) : (
                            <div
                                className='bg-white p-8 rounded-lg shadow-sm flex items-center justify-center h-64 mt-8'
                                data-testid='disruption-analysis-table-empty'
                            >
                                <div className='text-center text-gray-500'>
                                    <h4 className='text-lg font-medium mb-2'>
                                        Select Rosters to Compare
                                    </h4>
                                    <p className='text-sm'>
                                        Please select at least 2 rosters from the filters panel to
                                        view the comparison table.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RosterComparisonDisruptionAnalysis;
